import React, { useState, useEffect, useCallback, useContext } from "react";
import { TranslatorContext } from "../../../../context/Translator";
import { Link } from "react-router-dom";
import { Row, Col, Spinner, Alert, Button, Table, Modal, Form } from "react-bootstrap";
import axios from "axios";
import LabelFieldComponent from "../../../../components/fields/LabelFieldComponent";
import PageLayout from "../../../../layouts/PageLayout";

export default function StoreManager() {
    const { t } = useContext(TranslatorContext);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [stores, setStores] = useState([]);
    const [search, setSearch] = useState("");

    // Modal State
    const [showModal, setShowModal] = useState(false);
    const [editItem, setEditItem] = useState(null); // null for Add, Object for Edit
    const [name, setName] = useState("");
    const [fieldErrors, setFieldErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [alertMsg, setAlertMsg] = useState(null);

    const getAuthHeader = () => ({
        headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
    });

    const fetchStores = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/admin/store`,
                getAuthHeader()
            );
            // Handles direct array or wrapped data key
            const storeData = Array.isArray(response.data)
                ? response.data
                : response.data?.data || [];
            setStores(storeData);
        } catch (err) {
            console.error("Error fetching stores:", err);
            setError(err.response?.data?.message || "Failed to fetch stores list.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchStores();
    }, [fetchStores]);

    // Open Modal for Create or Edit
    const handleOpenModal = (store = null) => {
        setEditItem(store);
        setName(store ? store.name || "" : "");
        setFieldErrors({});
        setAlertMsg(null);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditItem(null);
        setName("");
        setFieldErrors({});
        setAlertMsg(null);
    };

    // Submit Handler
    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setFieldErrors({});
        setAlertMsg(null);

        const isEdit = Boolean(editItem);
        const url = isEdit
            ? `${process.env.REACT_APP_API_URL}/admin/store/${editItem.id}`
            : `${process.env.REACT_APP_API_URL}/admin/store`;

        try {
            const response = await axios.post(url, { name }, getAuthHeader());

            if (response.data?.success || response.status === 200 || response.status === 201) {
                handleCloseModal();
                fetchStores();
            }
        } catch (err) {
            if (err.response?.status === 422) {
                setFieldErrors(err.response.data.errors || {});
            } else {
                setAlertMsg({
                    type: "danger",
                    message: err.response?.data?.message || "Operation failed. Please try again.",
                });
            }
        } finally {
            setSubmitting(false);
        }
    };

    const filteredStores = stores.filter((s) =>
        s.name?.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card mb-4">
                        <div className="mc-breadcrumb d-flex justify-content-between align-items-center">
                            <div>
                                <h3 className="mc-breadcrumb-title">
                                    {t("Stores")} ({filteredStores.length})
                                </h3>
                                <ul className="mc-breadcrumb-list">
                                    <li className="mc-breadcrumb-item">
                                        <Link to="#" className="mc-breadcrumb-link">
                                            {t("Home")}
                                        </Link>
                                    </li>
                                    <li className="mc-breadcrumb-item">{t("Stores")}</li>
                                </ul>
                            </div>
                            <Button variant="primary" onClick={() => handleOpenModal()}>
                                <i className="material-icons me-1 vertical-middle">add</i>
                                {t("Add Store")}
                            </Button>
                        </div>
                    </div>
                </Col>

                <Col xl={12}>
                    <div className="mc-card">
                        {error && (
                            <Alert variant="danger" dismissible onClose={() => setError("")} className="mb-4">
                                {error}
                            </Alert>
                        )}

                        <Row className="g-3 align-items-end mb-4">
                            <Col md={6} xl={4}>
                                <LabelFieldComponent
                                    type="search"
                                    placeholder="Search by Store Name..."
                                    label="Search"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                />
                            </Col>
                        </Row>

                        {loading ? (
                            <div className="text-center py-5">
                                <Spinner animation="border" variant="primary" />
                                <p className="mt-2 text-muted">{t("Loading stores...")}</p>
                            </div>
                        ) : (
                            <div className="mc-table-responsive">
                                <Table hover className="mc-table align-middle">
                                    <thead className="mc-table-head primary">
                                        <tr>
                                            <th>Store Name</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody className="mc-table-body even">
                                        {filteredStores.length > 0 ? (
                                            filteredStores.map((store) => (
                                                <tr key={store.id}>
                                                    <td className="fw-semibold">{store.name || "N/A"}</td>
                                                    <td>
                                                        <div className="mc-table-action">
                                                            <button
                                                                type="button"
                                                                className="material-icons edit border-0 bg-transparent text-primary"
                                                                onClick={() => handleOpenModal(store)}
                                                                title="Edit"
                                                            >
                                                                edit
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={3} className="text-center py-4">
                                                    {t("No stores found")}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        )}
                    </div>
                </Col>
            </Row>

            {/* Add / Edit Modal */}
            <Modal show={showModal} onHide={handleCloseModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {editItem ? t("Edit Store") : t("Add Store")}
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit}>
                    <Modal.Body>
                        {alertMsg && (
                            <Alert variant={alertMsg.type} dismissible onClose={() => setAlertMsg(null)}>
                                {alertMsg.message}
                            </Alert>
                        )}

                        <Form.Group className="mb-3">
                            <Form.Label>Store Name *</Form.Label>
                            <Form.Control
                                type="text"
                                value={name}
                                onChange={(e) => {
                                    setName(e.target.value);
                                    if (fieldErrors.name) setFieldErrors({});
                                }}
                                isInvalid={!!fieldErrors.name}
                                placeholder="Enter store name"
                                required
                            />
                            <Form.Control.Feedback type="invalid">
                                {fieldErrors.name?.[0]}
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseModal}>
                            Cancel
                        </Button>
                        <Button variant="primary" type="submit" disabled={submitting}>
                            {submitting
                                ? editItem
                                    ? "Updating..."
                                    : "Saving..."
                                : editItem
                                    ? "Update Store"
                                    : "Save Store"}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </PageLayout>
    );
}